import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import EventDetailsModal from '@totem/components/services/threats/EventDetailsModal';
import { EventFilterOptions } from '@totem/components/services/threats/eventFilters/types';
import { EventStatusUpdateRequest } from '@totem/components/services/threats/Events';
import EventsContext from '@totem/components/services/threats/eventsContainer/EventsContext';
import TicketAssociateModal from '@totem/components/services/threats/ticketLinkModal/TicketAssociateModal';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { EventTicketAssociationPayload } from '@totem/types/event';
import { Event, EventAction, EventQueryResults } from '@totem/types/events';
import { isNotNull } from '@totem/utilities/common';
import {
  EVENTS_ENDPOINT,
  V2_EVENTS_ENDPOINT,
} from '@totem/utilities/endpoints';

export const containerConfiguration: FilterField[] = [
  {
    key: 'eventTime',
    label: 'Event Time',
    sortField: 'eventTime',
    filterField: 'eventTime',
    multiple: true,
    allowPersistence: false,
    hideFilterChips: true,
    values: [],
  },
  {
    key: 'lastOccurrence',
    label: 'Last Occur.',
    sortField: 'lastOccurrence',
    filterField: 'lastOccurrence',
    multiple: true,
    allowPersistence: false,
    values: [],
  },
  {
    key: 'severity',
    label: 'Severity',
    sortField: 'severity',
    filterField: 'severity',
    multiple: true,
    values: [],
  },
  {
    key: 'sourceType',
    label: 'Source',
    sortField: 'sourceType',
    filterField: 'sourceType',
    multiple: true,
    values: [],
  },
  {
    key: 'sourceSystem',
    label: 'Source System',
    sortField: 'sourceSystem',
    filterField: 'sourceSystem',
    multiple: true,
    values: [],
  },
  {
    key: 'eventType',
    label: 'Type',
    sortField: 'eventType',
    filterField: 'eventType',
    multiple: true,
    values: [],
  },
  {
    key: 'description',
    label: 'Description',
    sortField: 'description',
    filterField: 'description',
    operator: 'cscontains',
    multiple: false,
    value: '',
  },
  {
    key: 'region',
    label: 'Region',
    sortField: 'regionId',
    filterField: 'regionId',
    multiple: true,
    values: [],
  },
  {
    key: 'building',
    label: 'Building',
    sortField: 'buildingId',
    filterField: 'buildingId',
    multiple: true,
    values: [],
  },
  {
    key: 'controlSystem',
    label: 'Control System',
    sortField: 'controlSystemId',
    filterField: 'controlSystemId',
    multiple: true,
    values: [],
  },
  {
    key: 'device',
    label: 'Device',
    sortField: 'deviceId',
    filterField: 'deviceId',
    multiple: true,
    values: [],
  },
  {
    key: 'status',
    label: 'Status',
    sortField: 'status',
    filterField: 'status',
    multiple: true,
    values: [],
  },
  {
    key: 'reference',
    label: 'Reference',
    sortField: '',
    filterField: 'keys',
    multiple: true,
    values: [],
  },
];

type Props = {
  refresh?: boolean;
  onRecordTotalChanged?: (total: number) => void;
  onDataRefreshRequested?: () => void;
  actions?: EventAction[];
  children?: ReactNode;
  eventEndPoint?: string;
};

const EventsContainer = ({
  refresh,
  onRecordTotalChanged,
  onDataRefreshRequested,
  actions,
  children,
  eventEndPoint,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isSending, setIsSending] = useState(false);
  const [eventData, setEventData] = useState<EventQueryResults>(null);
  const [historyOnly, setHistoryOnly] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<Event>(null);
  const [showEventDetails, setShowEventDetails] = useState<boolean>(false);
  const [showTicketAssociation, setShowTicketAssociation] =
    useState<boolean>(false);

  const {
    buildParameters,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    setFilterOptions,
    isReady,
  } = useContext(InputContext);

  const fetchAbortController = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isNotNull(onRecordTotalChanged)) {
      onRecordTotalChanged(totalRecords);
    }
  }, [onRecordTotalChanged, totalRecords]);

  useEffect(() => {
    if (refresh && isNotNull(onDataRefreshRequested)) {
      setRefreshData(true);

      onDataRefreshRequested();
    }
  }, [refresh, onDataRefreshRequested]);

  useEffect(() => {
    setRefreshData(true);
  }, [
    eventEndPoint,
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
    userProfile.organization.id,
  ]);

  useEffect(() => {
    if (isReady) {
      setRefreshData(true);
    }
  }, [isReady, filterUpdateCount, pagingUpdateCount, sortingUpdateCount]);

  useEffect(() => {
    if (isSending) {
      return;
    }
    const endpoint = `${EVENTS_ENDPOINT}/filterOptions`;

    setIsSending(true);

    fetch(`${endpoint}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: EventFilterOptions) => {
        if (isNotNull(result)) {
          if (isNotNull(result.device)) {
            setFilterOptions(
              'device',
              result.device.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.building)) {
            setFilterOptions(
              'building',
              result.building.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.eventType)) {
            setFilterOptions(
              'eventType',
              result.eventType.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.region)) {
            setFilterOptions(
              'region',
              result.region.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.status)) {
            setFilterOptions(
              'status',
              result.status.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.severity)) {
            setFilterOptions('severity', [
              {
                text: 'OK',
                value: '0',
              },
              {
                text: 'Information',
                value: '1',
              },
              {
                text: 'Warning',
                value: '2',
              },
              {
                text: 'Minor',
                value: '3',
              },
              {
                text: 'Major',
                value: '4',
              },
              {
                text: 'Critical',
                value: '5',
              },
            ]);
          }

          if (isNotNull(result.sourceSystem)) {
            setFilterOptions(
              'sourceSystem',
              result.sourceSystem.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.sourceType)) {
            setFilterOptions(
              'sourceType',
              result.sourceType.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }
        }
      })
      .then(() => {
        setIsSending(false);
      });
  }, [userProfile.organization.id, getApiToken]);

  useEffect(() => {
    if (refreshData) {
      fetchAbortController.current?.abort();

      const controller = new AbortController();

      fetchAbortController.current = controller;

      setRefreshData(false);

      setIsLoading(true);
      const params: Params = buildParameters();

      const eventUrl = isNotNull(eventEndPoint)
        ? eventEndPoint
        : V2_EVENTS_ENDPOINT;

      fetch(`${eventUrl}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
      })
        .then((res) => res.json())
        .then((result: EventQueryResults) => {
          setEventData(result);

          setTotalRecords(result.paging.totalRecords);
        })
        .then(() => {
          setIsLoading(false);
        });

      setRefreshData(false);
    }
  }, [refreshData, refresh, getApiToken]);

  const sendEventStatusUpdate = useCallback(
    async (events: Event[]) => {
      if (isSending) {
        return;
      }

      setIsSending(true);

      setIsLoading(true);

      const params: Params = buildParameters();
      const request: EventStatusUpdateRequest = {
        params,
        events,
      };

      fetch(`${EVENTS_ENDPOINT}/status`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(request),
      })
        .then((res) => res.json())
        .then((result: EventQueryResults) => {
          setEventData(result);

          setTotalRecords(result.paging.totalRecords);
        })
        .then(() => {
          setIsSending(false);

          setIsLoading(false);
        });
    },
    [isSending],
  );

  const sendTicketCreate = useCallback(
    async (event: Event) => {
      if (isSending) {
        return;
      }

      setIsSending(true);

      setIsLoading(true);

      const params: Params = buildParameters();

      fetch(`${EVENTS_ENDPOINT}/${event.id}/createTicket`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
      })
        .then((res) => res.json())
        .then((result: EventQueryResults) => {
          setEventData(result);

          setTotalRecords(result.paging.totalRecords);
        })
        .then(() => {
          setIsSending(false);

          setIsLoading(false);
        });
    },
    [buildParameters, isSending],
  );

  const sendTicketAssociate = useCallback(
    async (eventId: string, ticketId: string, ticketNumber: string) => {
      if (isSending) {
        return;
      }

      const params: Params = buildParameters();
      const payload: EventTicketAssociationPayload = {
        ticketID: ticketId,
        ticketNumber,
        query: params,
      };

      setIsSending(true);

      setIsLoading(true);

      fetch(`${EVENTS_ENDPOINT}/${eventId}/associateTicket`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((result: EventQueryResults) => {
          setEventData(result);

          setTotalRecords(result.paging.totalRecords);
        })
        .then(() => {
          setIsSending(false);

          setIsLoading(false);
        });
    },
    [isSending],
  );

  const handleAssociateTicket = (
    eventId: string,
    ticket: TicketQueryResult,
  ) => {
    sendTicketAssociate(eventId, ticket.ticket.id, ticket.ticket.number);

    setShowTicketAssociation(false);
  };

  const handleAction = (action: string, event: Event) => {
    let actionHandled = false;
    if (isNotNull(actions)) {
      for (let idx = 0; idx < actions.length; idx++) {
        if (actions[idx].name === action) {
          if (actions[idx].replaceDefault === true) {
            actionHandled = true;

            actions[idx].action(action, event);
          }
        }
      }
    }
    if (!actionHandled) {
      // eslint-disable-next-line default-case
      switch (action) {
        case 'showEventDetails':
          setSelectedEvent(event);

          setShowEventDetails(true);
          break;
        case 'acknowledgeEvent':
          // eslint-disable-next-line no-case-declarations
          const eventAcknowledgeUpdate = {
            ...event,
            status: 'ACKNOWLEDGED',
          };
          sendEventStatusUpdate([eventAcknowledgeUpdate]);
          break;
        case 'clearEvent':
          // eslint-disable-next-line no-case-declarations
          const eventClearUpdate = {
            ...event,
            status: 'CLEARED',
          };
          sendEventStatusUpdate([eventClearUpdate]);
          break;
        case 'createTicket':
          sendTicketCreate(event);
          break;
        case 'showAssociateTicketDialog':
          setSelectedEvent(event);

          setShowTicketAssociation(true);
          break;
      }
    }
  };

  return (
    <EventsContext.Provider
      value={{
        loading: isLoading,
        eventData,
        totalRecords,
        onAction: handleAction,
        historyOnly: isNotNull(historyOnly) ? historyOnly : false,
        setHistoryOnly,
      }}
    >
      {children}
      {showEventDetails && isNotNull(selectedEvent) && (
        <EventDetailsModal
          event={selectedEvent}
          visible={showEventDetails}
          onClose={() => setShowEventDetails(false)}
        />
      )}
      {showTicketAssociation && isNotNull(selectedEvent) && (
        <TicketAssociateModal
          event={selectedEvent}
          visible={showTicketAssociation}
          onAssociateTicket={handleAssociateTicket}
          onClose={() => setShowTicketAssociation(false)}
        />
      )}
    </EventsContext.Provider>
  );
};

export default EventsContainer;
